import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import MainHeader from '../components/MainHeader';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';

type FeedbackType = 'bug' | 'suggestion';
type FeedbackStatus = 'nuevo' | 'revisando' | 'solucionado';
type FeedbackStatusLegacy = FeedbackStatus | 'revisado' | 'planificado' | 'resuelto' | 'descartado';

interface FeedbackItem {
  _id: string;
  tipo: FeedbackType;
  categoria: string;
  mensaje: string;
  nickname: string;
  modalidad: 'classic' | 'nohit';
  dificultad: 'normal' | 'dificil';
  nivelActual?: number;
  preguntaTipo?: string;
  preguntaInfo?: string;
  estado: FeedbackStatusLegacy;
  notaInterna?: string;
  asignadoA?: string;
  actualizadoPor?: string;
  updatedAt: string;
  createdAt: string;
}

interface AdminListResponse {
  items: FeedbackItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface AdminStatsResponse {
  total: number;
  recientes24h: number;
  porEstado: Array<{ _id: FeedbackStatus; total: number }>;
  porTipo: Array<{ _id: FeedbackType; total: number }>;
}

const ESTADOS: FeedbackStatus[] = ['nuevo', 'revisando', 'solucionado'];

const normalizeStatus = (status: FeedbackStatusLegacy): FeedbackStatus => {
  if (status === 'solucionado' || status === 'resuelto') return 'solucionado';
  if (status === 'revisando' || status === 'revisado' || status === 'planificado' || status === 'descartado') return 'revisando';
  return 'nuevo';
};

const statusBadgeClass = (status: FeedbackStatus) => {
  if (status === 'solucionado') return 'border-emerald-400/60 bg-emerald-500/15 text-emerald-300';
  if (status === 'revisando') return 'border-amber-400/60 bg-amber-500/15 text-amber-300';
  return 'border-sky-400/60 bg-sky-500/15 text-sky-300';
};

const getRequestErrorMessage = (err: any, fallback: string) => {
  if (err?.response?.data?.error) return err.response.data.error;
  if (err?.response?.status === 404) return 'El backend no tiene todavía las rutas admin desplegadas';
  if (err?.response?.status === 403) return 'Acceso denegado para esta cuenta';
  if (err?.message === 'Network Error') return 'No se pudo conectar con el servidor';
  return fallback;
};

const updateFeedbackStatus = async (
  itemId: string,
  payload: { estado: FeedbackStatus; notaInterna: string; asignadoA: string },
) => {
  const endpoints = [
    `/api/feedback/admin/${itemId}/status`,
    `/api/feedback/${itemId}/status`,
  ];

  let lastError: any = null;

  for (const endpoint of endpoints) {
    try {
      return await axios.patch(getApiUrl(endpoint), payload);
    } catch (err: any) {
      lastError = err;
      if (err?.response?.status !== 404) {
        throw err;
      }
    }
  }

  throw lastError;
};

const buildStatsFromItems = (rows: FeedbackItem[]): AdminStatsResponse => {
  const porEstadoMap = new Map<FeedbackStatus, number>();
  const porTipoMap = new Map<FeedbackType, number>();
  const cutoff = Date.now() - 24 * 60 * 60 * 1000;
  let recientes24h = 0;

  for (const row of rows) {
    const normalizedStatus = normalizeStatus(row.estado);
    porEstadoMap.set(normalizedStatus, (porEstadoMap.get(normalizedStatus) || 0) + 1);
    porTipoMap.set(row.tipo, (porTipoMap.get(row.tipo) || 0) + 1);
    if (new Date(row.createdAt).getTime() >= cutoff) recientes24h += 1;
  }

  return {
    total: rows.length,
    recientes24h,
    porEstado: Array.from(porEstadoMap.entries()).map(([key, total]) => ({ _id: key, total })),
    porTipo: Array.from(porTipoMap.entries()).map(([key, total]) => ({ _id: key, total })),
  };
};

const AdminFeedback: React.FC = () => {
  const { user, loading } = useAuth();
  const isAdminUser = Boolean(
    user && (user.role === 'admin' || user.email?.toLowerCase() === 'rusu.george82@gmail.com')
  );
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<'all' | FeedbackType>('all');
  const [filtroEstado, setFiltroEstado] = useState<'all' | FeedbackStatus>('all');
  const [searchText, setSearchText] = useState('');
  const [queryText, setQueryText] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const [statusById, setStatusById] = useState<Record<string, FeedbackStatus>>({});
  const [noteById, setNoteById] = useState<Record<string, string>>({});
  const [assignedById, setAssignedById] = useState<Record<string, string>>({});

  const estadoCount = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of stats?.porEstado || []) {
      map[row._id] = row.total;
    }
    return map;
  }, [stats]);

  const tipoCount = useMemo(() => {
    const map: Record<string, number> = {};
    for (const row of stats?.porTipo || []) {
      map[row._id] = row.total;
    }
    return map;
  }, [stats]);

  const loadStats = async () => {
    const { data } = await axios.get<AdminStatsResponse>(getApiUrl('/api/feedback/admin/stats'));
    setStats(data);
    return data;
  };

  const loadItems = async () => {
    const params: Record<string, string> = {};
    if (filtroTipo !== 'all') params.tipo = filtroTipo;
    if (filtroEstado !== 'all') params.estado = filtroEstado;
    if (queryText.trim()) params.q = queryText.trim();

    let loadedItems: FeedbackItem[] = [];

    try {
      const { data } = await axios.get<AdminListResponse>(getApiUrl('/api/feedback/admin/list'), { params });
      loadedItems = data.items;
    } catch (err: any) {
      // Compatibilidad: backend antiguo sin rutas /admin/*
      if (err?.response?.status === 404) {
        const { data } = await axios.get<FeedbackItem[]>(getApiUrl('/api/feedback'), { params });
        loadedItems = data;
      } else {
        throw err;
      }
    }

    setItems(loadedItems);

    const nextStatus: Record<string, FeedbackStatus> = {};
    const nextNote: Record<string, string> = {};
    const nextAssigned: Record<string, string> = {};

    for (const item of loadedItems) {
      nextStatus[item._id] = normalizeStatus(item.estado);
      nextNote[item._id] = item.notaInterna || '';
      nextAssigned[item._id] = item.asignadoA || '';
    }

    setStatusById(nextStatus);
    setNoteById(nextNote);
    setAssignedById(nextAssigned);

    return loadedItems;
  };

  useEffect(() => {
    if (loading) return;
    if (!isAdminUser) return;

    const fetchAll = async () => {
      setLoadingData(true);
      setError('');
      try {
        const loadedItems = await loadItems();
        try {
          await loadStats();
        } catch {
          // Si stats falla, al menos mostramos métricas derivadas del listado.
          setStats(buildStatsFromItems(loadedItems));
        }
      } catch (err: any) {
        setError(getRequestErrorMessage(err, 'No se pudo cargar el panel de administración'));
      } finally {
        setLoadingData(false);
      }
    };

    fetchAll();
  }, [loading, isAdminUser, filtroTipo, filtroEstado, queryText]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQueryText(searchText);
  };

  const saveItem = async (itemId: string) => {
    const estado = statusById[itemId];
    if (!estado) return;

    setSavingId(itemId);
    setError('');

    try {
      await updateFeedbackStatus(itemId, {
        estado,
        notaInterna: noteById[itemId] || '',
        asignadoA: assignedById[itemId] || '',
      });

      const refreshedItems = await loadItems();
      try {
        await loadStats();
      } catch {
        setStats(buildStatsFromItems(refreshedItems));
      }
    } catch (err: any) {
      setError(getRequestErrorMessage(err, 'No se pudo actualizar el feedback'));
    } finally {
      setSavingId(null);
    }
  };

  if (!loading && !user) {
    return <Navigate to="/" replace />;
  }

  if (!loading && user && !isAdminUser) {
    return (
      <div className="min-h-screen bg-[#0a0a1a]">
        <MainHeader />
        <div className="max-w-2xl mx-auto px-6 py-20">
          <div className="card-gaming text-center">
            <h1 className="font-gaming text-gaming-pink text-lg tracking-wider mb-3">ACCESO RESTRINGIDO</h1>
            <p className="font-main text-white/70">Esta zona es solo para cuentas administradoras.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      <MainHeader />

      <section className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        <div className="text-center">
          <h1 className="font-gaming text-gaming-cyan text-2xl tracking-widest mb-2">PANEL ADMIN · FEEDBACK</h1>
          <p className="font-main text-white/60">Gestiona bugs y sugerencias enviados por la comunidad.</p>
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-300 font-main text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-gaming p-4 text-center">
            <p className="font-main text-white/50 text-xs">TOTAL</p>
            <p className="font-gaming text-gaming-cyan text-xl">{stats?.total ?? '-'}</p>
          </div>
          <div className="card-gaming p-4 text-center">
            <p className="font-main text-white/50 text-xs">ÚLTIMAS 24H</p>
            <p className="font-gaming text-gaming-neon-orange text-xl">{stats?.recientes24h ?? '-'}</p>
          </div>
          <div className="card-gaming p-4 text-center">
            <p className="font-main text-white/50 text-xs">BUGS</p>
            <p className="font-gaming text-gaming-pink text-xl">{tipoCount.bug ?? 0}</p>
          </div>
          <div className="card-gaming p-4 text-center">
            <p className="font-main text-white/50 text-xs">SUGERENCIAS</p>
            <p className="font-gaming text-gaming-purple text-xl">{tipoCount.suggestion ?? 0}</p>
          </div>
        </div>

        <div className="card-gaming p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value as 'all' | FeedbackType)}
              className="w-full rounded-lg border border-white/20 bg-[#151b31] px-3 py-2 text-white font-main"
            >
              <option value="all">Todos los tipos</option>
              <option value="bug">Bugs</option>
              <option value="suggestion">Sugerencias</option>
            </select>

            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value as 'all' | FeedbackStatus)}
              className="w-full rounded-lg border border-white/20 bg-[#151b31] px-3 py-2 text-white font-main"
            >
              <option value="all">Todos los estados</option>
              {ESTADOS.map((estado) => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>

            <form onSubmit={handleSearch} className="md:col-span-2 flex gap-2">
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Buscar por mensaje, nickname o categoría"
                className="flex-1 rounded-lg border border-white/20 bg-[#151b31] px-3 py-2 text-white font-main"
              />
              <button type="submit" className="btn-primary px-4 py-2">Buscar</button>
            </form>
          </div>

          <div className="flex flex-wrap gap-2 text-xs font-main text-white/60">
            {ESTADOS.map((estado) => (
              <span key={estado} className="px-2 py-1 rounded border border-white/20 bg-white/5">
                {estado}: {estadoCount[estado] ?? 0}
              </span>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {loadingData ? (
            <div className="card-gaming text-center font-main text-white/60">Cargando...</div>
          ) : items.length === 0 ? (
            <div className="card-gaming text-center font-main text-white/60">No hay elementos con estos filtros.</div>
          ) : (
            items.map((item) => (
              <div
                key={item._id}
                className={`card-gaming p-4 space-y-3 cursor-pointer border-l-4 ${
                  normalizeStatus(statusById[item._id] || item.estado) === 'solucionado'
                    ? 'border-l-emerald-400'
                    : normalizeStatus(statusById[item._id] || item.estado) === 'revisando'
                      ? 'border-l-amber-400'
                      : 'border-l-sky-400'
                }`}
                onClick={() => setExpandedIds((prev) => ({ ...prev, [item._id]: !prev[item._id] }))}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className={`badge-${item.dificultad}`}>{item.dificultad}</span>
                    <span className="font-main text-xs px-2 py-1 rounded border border-white/20 bg-white/5 text-white/80 uppercase">
                      {item.tipo === 'bug' ? 'bug' : 'sugerencia'}
                    </span>
                    <span className="font-main text-xs text-white/50">{item.modalidad} · {item.categoria}</span>
                    <span className={`font-main text-[11px] px-2 py-1 rounded border uppercase ${statusBadgeClass(normalizeStatus(statusById[item._id] || item.estado))}`}>
                      {normalizeStatus(statusById[item._id] || item.estado)}
                    </span>
                  </div>
                  <span className="font-main text-xs text-white/50">
                    {new Date(item.createdAt).toLocaleString('es-ES')}
                  </span>
                </div>

                <p className="font-main text-white/90 whitespace-pre-wrap">{item.mensaje}</p>

                {expandedIds[item._id] && (
                  <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <p className="font-main text-white/50 text-xs">Jugador</p>
                        <p className="font-main text-white">{item.nickname || 'Invitado'}</p>
                      </div>
                      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <p className="font-main text-white/50 text-xs">Contexto</p>
                        <p className="font-main text-white">Nivel {item.nivelActual || '-'} · {item.preguntaTipo || '-'}</p>
                      </div>
                      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                        <p className="font-main text-white/50 text-xs">Pregunta</p>
                        <p className="font-main text-white truncate">{item.preguntaInfo || '-'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <select
                        value={statusById[item._id] || normalizeStatus(item.estado)}
                        onChange={(e) => setStatusById((prev) => ({ ...prev, [item._id]: e.target.value as FeedbackStatus }))}
                        className="rounded-lg border border-white/20 bg-[#151b31] px-3 py-2 text-white font-main"
                      >
                        {ESTADOS.map((estado) => (
                          <option key={estado} value={estado}>{estado}</option>
                        ))}
                      </select>

                      <input
                        value={assignedById[item._id] || ''}
                        onChange={(e) => setAssignedById((prev) => ({ ...prev, [item._id]: e.target.value }))}
                        placeholder="Asignado a"
                        className="rounded-lg border border-white/20 bg-[#151b31] px-3 py-2 text-white font-main"
                      />

                      <input
                        value={noteById[item._id] || ''}
                        onChange={(e) => setNoteById((prev) => ({ ...prev, [item._id]: e.target.value }))}
                        placeholder="Nota interna"
                        className="md:col-span-2 rounded-lg border border-white/20 bg-[#151b31] px-3 py-2 text-white font-main"
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-main text-xs text-white/45">
                        Última actualización: {item.updatedAt ? new Date(item.updatedAt).toLocaleString('es-ES') : '-'} · Por: {item.actualizadoPor || '-'}
                      </p>
                      <button
                        onClick={() => saveItem(item._id)}
                        disabled={savingId === item._id}
                        className="btn-primary text-xs px-4 py-2 disabled:opacity-50"
                      >
                        {savingId === item._id ? 'Guardando...' : 'Guardar cambios'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminFeedback;
